import type { IStoriesFolder } from "@/shared/types";

type TFolderLike = Pick<IStoriesFolder, "_id"> &
  Partial<Pick<IStoriesFolder, "parentId">>;

/**
 * Widens `matchedIds` to every folder that owns at least one of them somewhere
 * below it — i.e. a parent folder counts as "matched" when a nested subfolder
 * matches.
 *
 * The analytics folder filter uses this to decide which folders are worth
 * offering: stories live in the innermost folder, so matching folder ids
 * exactly hid every parent (`Client Name`) and left only the leaf (`Job Name`)
 * selectable. Walking up from each match is O(depth) per match instead of
 * re-walking each folder's subtree.
 */
export const expandFolderMatchesToAncestors = (
  folders: TFolderLike[],
  matchedIds: readonly string[],
): Set<string> => {
  const parentById = new Map<string, string | null | undefined>();
  for (const folder of folders) parentById.set(folder._id, folder.parentId);

  const result = new Set<string>();
  for (const matchedId of matchedIds) {
    if (!matchedId) continue;
    // Guards a cyclic parent chain: stop as soon as we revisit an id we already
    // recorded on this walk.
    const seen = new Set<string>();
    let current: string | null | undefined = matchedId;
    while (current && !seen.has(current)) {
      seen.add(current);
      result.add(current);
      current = parentById.get(current);
    }
  }

  return result;
};

/**
 * Prunes a nested folder tree down to the folders worth showing for
 * `searchTerm`, matching names at any depth.
 *
 * Filtering the top level alone made nesting decide what was searchable: a
 * matching subfolder stayed hidden unless its parent's name matched too, and a
 * parent that matched dumped its whole subtree into the results. A folder is
 * kept here when its own name matches, or when it is an ancestor of a match —
 * so a deep match stays reachable through its parents, and those parents bring
 * along only the branches that lead to a match.
 *
 * An empty or whitespace-only term keeps the tree as-is. The input is left
 * untouched; pruned branches are returned as new nodes.
 */
export const filterFolderTreeByName = <
  TNode extends TFolderLike & { name: string; children: TNode[] },
>(
  tree: readonly TNode[],
  searchTerm: string,
): TNode[] => {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return [...tree];

  const flattened: TNode[] = [];
  const flatten = (nodes: readonly TNode[]) => {
    for (const folder of nodes) {
      flattened.push(folder);
      flatten(folder.children);
    }
  };
  flatten(tree);

  const matchedIds = flattened
    .filter((folder) => folder.name.toLowerCase().includes(term))
    .map((folder) => folder._id);

  // Reuses the ancestor walk so both folder UIs agree on when a parent counts.
  const reachableIds = expandFolderMatchesToAncestors(flattened, matchedIds);

  const prune = (nodes: readonly TNode[]): TNode[] =>
    nodes
      .filter((folder) => reachableIds.has(folder._id))
      .map((folder) => ({ ...folder, children: prune(folder.children) }));

  return prune(tree);
};

type TNamedFolderLike = TFolderLike & { name: string };

export interface IFolderPathInfo {
  /** Ancestor names, outermost first. Empty for a top-level folder. */
  ancestors: string[];
  /** True when another folder in the same list sits directly below this one. */
  hasChildren: boolean;
}

/**
 * Indexes a flat folder list by id, resolving each folder's ancestor names and
 * whether anything nests under it.
 *
 * The analytics folder filter is a flat, searchable dropdown rather than a
 * tree, so a row has to carry its own context: since parent folders became
 * selectable, two subfolders called `Q1` under different clients rendered as
 * two identical rows. Resolving the whole list in one pass keeps that per-row
 * lookup out of the render loop.
 *
 * Cycle-safe: a folder that is transitively its own ancestor stops the walk
 * rather than looping.
 */
export const buildFolderPathIndex = <T extends TNamedFolderLike>(
  folders: readonly T[],
): Map<string, IFolderPathInfo> => {
  const byId = new Map<string, T>();
  for (const folder of folders) byId.set(folder._id, folder);

  const parentsWithChildren = new Set<string>();
  for (const folder of folders) {
    if (folder.parentId && byId.has(folder.parentId)) {
      parentsWithChildren.add(folder.parentId);
    }
  }

  const index = new Map<string, IFolderPathInfo>();
  for (const folder of folders) {
    const ancestors: string[] = [];
    const seen = new Set<string>([folder._id]);
    let currentId = folder.parentId;

    while (currentId && !seen.has(currentId)) {
      seen.add(currentId);
      const parent = byId.get(currentId);
      if (!parent) break;
      ancestors.unshift(parent.name);
      currentId = parent.parentId;
    }

    index.set(folder._id, {
      ancestors,
      hasChildren: parentsWithChildren.has(folder._id),
    });
  }

  return index;
};

/**
 * Renders a folder path as a single line, eliding the middle when it is deeper
 * than two levels: `Client A / … / Round 2`.
 *
 * Nesting depth is unbounded, and the dropdown row is far too narrow for a full
 * path. The outermost segment says whose data this is and the innermost says
 * what was clicked; the levels between are the least identifying, so they are
 * the ones to drop. This mirrors `calculateTruncatedPath` in the Stories
 * breadcrumbs (`src/pages/stories/components/breadcrumbs.tsx`) so both surfaces
 * shorten a path the same way.
 */
export const formatFolderPath = (segments: readonly string[]): string => {
  if (segments.length === 0) return "";
  if (segments.length <= 2) return segments.join(" / ");
  return `${segments[0]} / … / ${segments[segments.length - 1]}`;
};

export interface IOrderedFolder<T> {
  folder: T;
  /** Nesting depth among the folders actually present in the input. */
  depth: number;
}

/**
 * Reorders a flat folder list so each folder is followed immediately by its
 * own subtree, depth-first, with siblings keeping their incoming order.
 *
 * The list arrives sorted flat by `order`/`createdAt`, which scatters a parent
 * away from its children — `SEC`, `Primal`, `CFO (in Primal)`, `CFO (in SEC)`
 * reads as four unrelated rows. Depth is measured against the folders present
 * here rather than the true tree, so a folder whose parent was filtered out
 * sits at the top level instead of rendering as an orphan indented under
 * nothing.
 *
 * Cycle-safe: a folder never reached by the walk is appended at depth 0 rather
 * than dropped.
 */
export const orderFoldersByHierarchy = <T extends TFolderLike>(
  folders: readonly T[],
): IOrderedFolder<T>[] => {
  const present = new Set(folders.map((folder) => folder._id));

  const childrenByParent = new Map<string, T[]>();
  const roots: T[] = [];
  for (const folder of folders) {
    const parentId = folder.parentId;
    if (!parentId || !present.has(parentId)) {
      roots.push(folder);
      continue;
    }
    const siblings = childrenByParent.get(parentId);
    if (siblings) siblings.push(folder);
    else childrenByParent.set(parentId, [folder]);
  }

  const ordered: IOrderedFolder<T>[] = [];
  const emitted = new Set<string>();

  const walk = (folder: T, depth: number) => {
    if (emitted.has(folder._id)) return;
    emitted.add(folder._id);
    ordered.push({ folder, depth });
    for (const child of childrenByParent.get(folder._id) ?? []) {
      walk(child, depth + 1);
    }
  };

  for (const root of roots) walk(root, 0);
  // Anything left is part of a parent cycle; keep it visible.
  for (const folder of folders) {
    if (!emitted.has(folder._id)) ordered.push({ folder, depth: 0 });
  }

  return ordered;
};
